import { useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { DayPicker } from 'react-day-picker';

// Import constants
import { MAX_DAYS } from '../../constants/schedule';

// Import types
import type { DateRange } from '../../types/date';

type Props = {
  isOwner: boolean;
  pickerRange: DateRange;
  onChange: (range: DateRange) => void;
};

/**
 * Encapsulates the DayPicker with business rules:
 * - Disallow selecting past days
 * - Clamp range to max 10 days inclusive
 * - Enforce owner-only interactions (non-owners get an alert)
 */
function DateRangePickerPanel(props: Props) {
  const { isOwner, pickerRange, onChange } = props;

  const handleSelect = (dateRange: DateRange | undefined) => {
    if (!isOwner) {
      toast.error('Only the owner can configure the grid');
      return;
    }

    if (!dateRange) {
      onChange({ from: undefined, to: undefined });
      return;
    }

    // Enforce: could not select past dates older than 3 months
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const { from, to } = dateRange;
    if (!from || !to) return;

    const fromDay = new Date(from);
    fromDay.setHours(0, 0, 0, 0);
    const toDay = new Date(to);
    toDay.setHours(0, 0, 0, 0);

    // Enforce max 10 days inclusive
    const msPerDay = 24 * 60 * 60 * 1000;
    const days =
      Math.floor((toDay.getTime() - fromDay.getTime()) / msPerDay) + 1;
    if (days > MAX_DAYS) {
      toast.error(`You can select at most ${MAX_DAYS} days`);
      return;
    }

    onChange({ from: fromDay, to: toDay });
  };

  const minAllowed = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const min = new Date(today);
    min.setMonth(today.getMonth() - 3);
    return min;
  }, []);

  return (
    <DayPicker
      className="flex justify-center"
      captionLayout="dropdown-months"
      mode="range"
      selected={pickerRange}
      disabled={{ before: minAllowed }}
      onDayClick={() => {
        if (!isOwner) toast.error('Only the owner can configure the grid');
      }}
      onSelect={(dateRange) => handleSelect(dateRange)}
      showOutsideDays
      fixedWeeks
    />
  );
}

export default DateRangePickerPanel;
